import React from 'react';

interface BrowserSettingItem {
  label: string;
  description: string;
  checked: boolean;
  required: boolean; // if true, user must check it
}

const items: BrowserSettingItem[] = [
  {
    label: 'Enable JavaScript',
    description: 'Essential for interactive assessment elements',
    checked: true,
    required: true,
  },
  {
    label: 'Disable pop-up blockers',
    description: 'Required for opening examination windows',
    checked: true,
    required: true,
  },
  {
    label: 'Allow third-party cookies',
    description: 'Used for session validation and security',
    checked: true,
    required: false,
  },
  {
    label: 'Microphone & Camera Access',
    description: 'Only for proctored sections of the test',
    checked: true,
    required: true,
  },
];

export const BrowserSettingsCard: React.FC = () => {
  return (
    <div className='w-full rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden text-left'>
      <div className='px-5 py-4'>
        <h3 className='text-base font-bold text-gray-900 mb-1'>
          Configuration Required
        </h3>
        <p className='text-[11px] text-gray-500 mb-3 leading-snug'>
          To ensure the assessment platform runs smoothly and records your
          progress accurately, please verify the following settings in your
          browser.
        </p>

        { /* Checklist */ }
        <div className='flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden'>
          { items.map((item, index) => (
            <div
                key={index}
                className='flex items-center gap-3 px-4 py-3 text-left'
            >
              { /* Checkbox */ }
              <div
                  className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                    item.checked
                      ? 'bg-teal-600 border-teal-600'
                      : 'border-gray-300 bg-white'
                  }`}
              >
                { item.checked && (
                  <svg
                      className='w-3 h-3 text-white'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth={3}
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M5 13l4 4L19 7'
                    />
                  </svg>
                ) }
              </div>

              <div className='flex-1 min-w-0'>
                <p className='text-[12px] font-semibold text-gray-800 leading-tight'>
                  { item.label }
                </p>
                <p className='text-[10px] text-gray-500 mt-0.5 leading-tight'>
                  { item.description }
                </p>
              </div>

              { item.required && !item.checked && (
                <span className='text-[9px] font-bold uppercase text-red-400 tracking-wide flex-shrink-0'>
                  Required
                </span>
              ) }
            </div>
          )) }
        </div>

        { /* VPN note */ }
        <div className='mt-3 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5'>
          <svg
              className='w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5'
              fill='currentColor'
              viewBox='0 0 20 20'
          >
            <path
                fillRule='evenodd'
                d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                clipRule='evenodd'
            />
          </svg>
          <p className='text-[10px] text-amber-700 leading-snug'>
            If you are using a corporate VPN or restricted network, please
            ensure these domains are whitelisted.
          </p>
        </div>
      </div>
    </div>
  );
};
